import { useEffect, useRef, useState } from 'react';
import { useDesignStore } from '../store/designStore';
import { safeGetItem } from '../lib/safeStorage';
import {
  getRequestedColorVariantNameFromSearch,
  getRequestedPrintMethodFromSearch,
  getRequestedPrintPositionsFromSearch,
  getRequestedPrintMethodByPositionFromSearch,
  getRequestedPrintColorByPositionFromSearch,
} from './useProductSetup';

const API_BASE = '/api';

function authHeaders(): Record<string, string> {
  const token = safeGetItem('session', 'customedge_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Loads a design shared via `?design=<id>` (see DesignerHeader's Share button)
// once the initial side's canvas exists — mirrors MyDesignsTool.tsx's own
// loadDesign(), the only other place a saved design is restored into the
// live canvas. Runs once; the URL param is stripped immediately after so a
// later manual "Save" doesn't keep re-applying it or leak into share links
// copied from the address bar afterward.
//
// Two network round trips, not one, because of a real ordering bug found via
// PX113334 (a product with zero base imageSides — every side lives under a
// colorVariant): by the time the first canvas mounts, useProductSetup has
// already auto-selected the *first* color variant/left print method unset,
// so `sides`/`activeSideKey` are keyed off the wrong variant before this
// hook ever gets to run. Restoring colorVariantId/printMethod re-derives
// `sides` (see useProductSetup's second effect) and remounts canvases under
// the correct keys — `loadFromTemplate`'s jsonObjects restore must wait for
// that remount, or it silently targets a sideKey the design was never saved
// under and no-ops with the canvas staying blank.
export function useSharedDesignLoader() {
  const {
    canvasInstances, activeSideKey, loadFromTemplate, setSavedDesignId, setSelectedVariantOptions,
    setActiveColorVariant, setSelectedPrintMethod, setSelectedPrintColorOption, colorVariants,
    setSelectedPrintPositions, setSelectedPrintMethodByPosition, setSelectedPrintColorByPosition,
    setIsRestoringSavedDesign,
  } = useDesignStore();
  const attempted = useRef(false);
  // State, not a ref — Phase 2 (below) depends on this being set to decide
  // when to apply jsonObjects. A ref's mutation is invisible to React's
  // effect scheduler, so Phase 2 previously only happened to re-run after
  // this was populated because setActiveColorVariant's OWN (separately buggy)
  // unconditional canvasInstances-clear-on-every-call gave Phase 2's effect
  // a reason to fire again afterward. Fixing that bug (designStore.ts's
  // setActiveColorVariant now skips the clear when the id isn't actually
  // changing — the common case here, since useProductSetup's own `?colorVariant=`
  // URL-param resolution already set the correct variant before this fetch
  // resolves) removed that incidental trigger and exposed this: Phase 2 could
  // fetch successfully, populate this ref, and then never run again — the
  // canvas would stay permanently blank despite the correct data being ready.
  // Confirmed live 2026-08-03 via the Promofit React PDP's "Bewerken" resume flow.
  const [detail, setDetail] = useState<any>(null);

  // Phase 1: fetch the design once the first canvas mounts, and apply every
  // selection that can change *which* sides/canvases exist (color variant,
  // print method/color) — but not jsonObjects yet, since the canvas(es) it
  // targets may not exist under their final keys until after this applies.
  useEffect(() => {
    if (attempted.current) return;
    const designId = new URLSearchParams(window.location.search).get('design');
    if (!designId) { attempted.current = true; return; }
    if (!canvasInstances.get(activeSideKey)) return; // wait for the initial canvas to mount

    attempted.current = true;
    // Held true until Phase 2 (below) applies jsonObjects or determines
    // there's nothing to load — see designStore.ts's field comment for why
    // useFabricCanvas.ts's color-count-switch effect needs to stay quiet for
    // the whole restore sequence, not just this fetch.
    setIsRestoringSavedDesign(true);

    const params = new URLSearchParams(window.location.search);
    params.delete('design');
    const newSearch = params.toString() ? `?${params}` : '';
    window.history.replaceState({}, '', window.location.pathname + newSearch);

    // Snapshot which selection fields the CURRENT URL already brought an
    // explicit fresh value for — e.g. the PDP's "Bewerken" resume link
    // (UploadImages/index.jsx's handleClick) sends BOTH `?design=<id>` (to
    // continue the same pending design, not orphan a second one) AND the
    // customer's freshly re-picked `?colorVariant=`/`?printPositions=`/
    // `?printMethodByPosition=`/`?printColorByPosition=` in the very same
    // launch URL, whenever they changed their PDP selection before
    // reopening. useProductSetup's own effect already applied those fresh
    // values (it runs off `config`, which resolves before this fetch can
    // land). Without this guard, the fetch below unconditionally overwrote
    // every one of those with the design's OLD saved selection — a customer
    // changing position/variant on the PDP and clicking "Bewerken" again
    // always saw the design reopen on the ORIGINAL position/variant, not the
    // new one just picked, because this restore always ran last and always
    // won. Reported live 2026-08-11: "updated the position and retried — it
    // shows the old design and old option/variant, it should populate the
    // new selection." A field with no fresh URL value (the common share-
    // link/"My Designs" reopen case, or a resume where that particular
    // field wasn't touched) still restores from the saved design exactly as
    // before — this only skips fields the current URL explicitly overrides.
    const search = window.location.search;
    const hasFreshColorVariant = !!getRequestedColorVariantNameFromSearch(search);
    const hasFreshPrintMethod = !!getRequestedPrintMethodFromSearch(search);
    const hasFreshPositions = getRequestedPrintPositionsFromSearch(search).length > 0;
    const hasFreshMethodByPosition = Object.keys(getRequestedPrintMethodByPositionFromSearch(search)).length > 0;
    const hasFreshColorByPosition = Object.keys(getRequestedPrintColorByPositionFromSearch(search)).length > 0;

    fetch(`${API_BASE}/designs/${designId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(fetchedDetail => {
        setDetail(fetchedDetail);
        setSavedDesignId(designId);

        let productInfo: any = {};
        try { productInfo = JSON.parse(fetchedDetail.productInfo ?? '{}'); } catch { /* malformed — keep defaults */ }

        // Restore the Object Variant selection saved with the design so the
        // 3D preview / mini widget reopen on the variant the customer
        // picked, not the model's default appearance.
        if (productInfo.selectedVariantOptions && Object.keys(productInfo.selectedVariantOptions).length) {
          setSelectedVariantOptions(productInfo.selectedVariantOptions);
        }

        // Restore the vendor Product Color Variant pick — without this, a
        // product with no base imageSides (every side lives under a
        // colorVariant) stays on whatever variant useProductSetup
        // auto-selected first, and the jsonObjects restore below silently
        // targets the wrong sideKey. Skipped when the URL already carries a
        // fresh `?colorVariant=` — see the fresh-URL-wins guard above.
        if (!hasFreshColorVariant && productInfo.colorVariantId) setActiveColorVariant(productInfo.colorVariantId, true);

        // Restore print method / print color (ProductOptionsModal's other
        // two pills) — same "which sides exist" dependency as colorVariantId
        // above, see useProductSetup's per-side/per-area printMethod filter.
        try {
          const pm = fetchedDetail.printingMethodDetails ? JSON.parse(fetchedDetail.printingMethodDetails) : null;
          if (!hasFreshPrintMethod && pm?.method) setSelectedPrintMethod(pm.method);
          if (pm?.printColorOption != null) setSelectedPrintColorOption(pm.printColorOption);
          // positions/methodByPosition/colorByPosition — same gap and same
          // fix as useDesignWip.ts's identical block (see that hook's own
          // comment for the full rationale): without these, a shared `?design=`
          // link's own position filter never engages and every side on the
          // product renders instead of just the ones the customer selected.
          // Each one individually skipped when the URL already brought its
          // own fresh value — see the fresh-URL-wins guard above.
          if (!hasFreshPositions && pm?.positions?.length) setSelectedPrintPositions(pm.positions);
          if (!hasFreshMethodByPosition && pm?.methodByPosition && Object.keys(pm.methodByPosition).length) {
            setSelectedPrintMethodByPosition(pm.methodByPosition);
          }
          if (!hasFreshColorByPosition && pm?.colorByPosition && Object.keys(pm.colorByPosition).length) {
            setSelectedPrintColorByPosition(pm.colorByPosition);
          }
        } catch { /* malformed printingMethodDetails — leave unset */ }
      })
      .catch(() => {
        // Shared design missing/inaccessible — fall back to a blank design silently.
        setIsRestoringSavedDesign(false); // Phase 2 will never get a detail to apply
      });
  }, [
    canvasInstances, activeSideKey, setSavedDesignId, setSelectedVariantOptions, setActiveColorVariant,
    setSelectedPrintMethod, setSelectedPrintColorOption,
    setSelectedPrintPositions, setSelectedPrintMethodByPosition, setSelectedPrintColorByPosition,
    setIsRestoringSavedDesign,
  ]);

  // Phase 2: once the fetch above has landed AND a canvas exists at
  // activeSideKey, apply the saved canvas JSON. `detail` is real state (not a
  // ref) specifically so that Phase 1's fetch resolving is itself enough to
  // re-run this effect — a canvas can already be mounted under the right key
  // by the time the fetch lands (e.g. a single-color-variant product, where
  // setActiveColorVariant/setSelectedPrintMethod are no-ops and never force a
  // remount), in which case there is no OTHER state change left to trigger
  // this effect again. Guarded by its own ref so it still only applies once
  // even though canvasInstances can also change independently (a real
  // colorVariant-driven remount on a multi-variant product).
  const jsonApplied = useRef(false);
  useEffect(() => {
    if (jsonApplied.current) return;
    if (!detail) return; // Phase 1's fetch hasn't landed yet — isRestoringSavedDesign stays true
    if (!detail.jsonObjects) {
      // Phase 1 DID land, but this design has no saved canvas content — the
      // restore sequence is genuinely over here.
      jsonApplied.current = true;
      setIsRestoringSavedDesign(false);
      return;
    }
    if (!canvasInstances.get(activeSideKey)) return; // wait for the (possibly remounted) canvas
    jsonApplied.current = true;
    loadFromTemplate(detail.jsonObjects);
    setIsRestoringSavedDesign(false);
  }, [detail, canvasInstances, activeSideKey, loadFromTemplate, colorVariants, setIsRestoringSavedDesign]);
}
